<template>
  <div class="NorceCheckoutPayment">
    <!-- Adapter Selector (optional, shown when multiple PSP adapters available) -->
    <div
      v-if="showAdapterSelector && hasMultiplePspAdapters && !isNonPsp"
      class="adapter-selector flex gap-8 mb-16"
    >
      <button
        v-for="adapter in pspAdapters"
        :key="adapter"
        class="adapter-btn flex-1 py-12 px-16 border text-center type-sm transition-colors flex items-center justify-center gap-8"
        :class="{
          'border-dark bg-lightest font-medium': currentAdapter === adapter,
          'border-light bg-white hover:border-dark': currentAdapter !== adapter,
          'opacity-50 cursor-not-allowed': isSwitchingAdapter,
        }"
        :disabled="isSwitchingAdapter"
        type="button"
        @click="switchAdapter(adapter)"
      >
        <img
          v-if="adapterLogos[adapter]"
          :src="adapterLogos[adapter]"
          :alt="getAdapterLabel(adapter)"
          class="adapter-logo"
        >
        <span v-else>{{ getAdapterLabel(adapter) }}</span>
      </button>
    </div>

    <!-- Loading state during adapter switch -->
    <div v-if="isSwitchingAdapter" class="switching-loader flex items-center justify-center py-32">
      <div class="loading-spinner" />
      <span class="ml-12 type-sm text-darker">{{ resolvedSwitchingText }}</span>
    </div>

    <!-- Adyen Drop-in -->
    <NorceCheckoutAdyen
      v-else-if="isAdyen && !isInitializing"
      ref="adyenDropinRef"
      :basket-uuid="basketUuid"
      :can-complete-purchase="props.canCompletePurchase"
      :auto-initialize="false"
      @success="handleSuccess"
      @error="handleError"
    />

    <!-- Klarna Checkout -->
    <NorceCheckoutKlarna
      v-else-if="isKlarna"
      ref="klarnaCheckoutRef"
      :klarna-payment="(payment as KlarnaPaymentData)"
      :basket-uuid="basketUuid"
      :order-id="orderId || undefined"
      :payment-id="paymentId || undefined"
      :is-loading="isInitializing"
      :loading-text="resolvedLoadingText"
      :can-complete-purchase="props.canCompletePurchase"
      @error="handleError"
      @retry="retryInitialize"
    />

    <!-- Walley Checkout -->
    <NorceCheckoutWalley
      v-else-if="isWalley"
      ref="walleyCheckoutRef"
      :walley-payment="(payment as WalleyPaymentData)"
      :basket-uuid="basketUuid"
      :order-id="orderId || undefined"
      :payment-id="paymentId || undefined"
      :is-loading="isInitializing"
      :loading-text="resolvedLoadingText"
      :can-complete-purchase="props.canCompletePurchase"
      @error="handleError"
      @retry="retryInitialize"
    />

    <!-- Non-PSP Payment (free orders, invoice) -->
    <NorceCheckoutNonPsp
      v-else-if="isNonPsp && !isInitializing"
      ref="nonPspCheckoutRef"
      :basket-uuid="basketUuid"
      :can-complete-purchase="props.canCompletePurchase"
      :auto-initialize="false"
      @success="handleSuccess"
      @error="handleError"
    />

    <!-- Fallback/Error state -->
    <div v-else-if="error && !isRetryingInit" class="payment-error p-16 bg-criticalLight border border-criticalDark">
      <p class="type-sm text-criticalDark">{{ error }}</p>
      <button
        class="btn btn--sm mt-12"
        type="button"
        @click="retryInitialize"
      >
        {{ resolvedRetryText }}
      </button>
    </div>

    <!-- Initial loading state -->
    <div v-else-if="isInitializing || isRetryingInit" class="initial-loader flex items-center justify-center py-32">
      <div class="loading-spinner" />
      <span class="ml-12 type-sm text-darker">{{ resolvedLoadingText }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import useNorceCheckout from './useNorceCheckout';
import NorceCheckoutAdyen from './NorceCheckoutAdyen.vue';
import NorceCheckoutKlarna from './NorceCheckoutKlarna.vue';
import NorceCheckoutWalley from './NorceCheckoutWalley.vue';
import NorceCheckoutNonPsp from './NorceCheckoutNonPsp.vue';
import { useCartStore } from '~/store/cart';
import type { PaymentAdapter, NorceOrderResult, KlarnaPaymentData, WalleyPaymentData } from './types';

const { $t } = useNuxtApp();

const props = withDefaults(defineProps<{
  basketUuid: string;
  showAdapterSelector?: boolean;
  adapterLabels?: Record<string, string>;
  loadingText?: string;
  switchingText?: string;
  retryText?: string;
  autoInitialize?: boolean;
  canCompletePurchase?: boolean;
}>(), {
  showAdapterSelector: true,
  adapterLabels: undefined,
  loadingText: undefined,
  switchingText: undefined,
  retryText: undefined,
  autoInitialize: true,
  canCompletePurchase: true,
});

// Adapter logos mapping
const adapterLogos: Record<string, string> = {
  adyen: '/logos/adyen_logo.png',
  klarna: '/logos/kustom_logo.svg',
  walley: '/logos/walley_logo.png',
};

// Computed translations with prop overrides
const resolvedLoadingText = computed(() => props.loadingText ?? $t('checkout.norceCheckout.loading'));
const resolvedSwitchingText = computed(() => props.switchingText ?? $t('checkout.norceCheckout.switching'));
const resolvedRetryText = computed(() => props.retryText ?? $t('checkout.norceCheckout.retry'));

const emit = defineEmits<{
  (e: 'success', basketId: string, order?: NorceOrderResult): void;
  (e: 'error', message: string): void;
  (e: 'adapter-changed', adapter: PaymentAdapter): void;
  (e: 'initialized'): void;
}>();

// Refs for child components
const adyenDropinRef = ref<InstanceType<typeof NorceCheckoutAdyen> | null>(null);
const klarnaCheckoutRef = ref<InstanceType<typeof NorceCheckoutKlarna> | null>(null);
const walleyCheckoutRef = ref<InstanceType<typeof NorceCheckoutWalley> | null>(null);
const nonPspCheckoutRef = ref<InstanceType<typeof NorceCheckoutNonPsp> | null>(null);

// Norce Checkout composable
const norceCheckout = useNorceCheckout();
const cartStore = useCartStore();

// TEMPORARY: keeps the loading state (instead of flashing the error screen) while the
// export-blocked initialize retry runs. Remove with the retry stopgap below.
const isRetryingInit = ref(false);

// Computed properties from composable
const isAdyen = computed(() => norceCheckout.isAdyen.value);
const isKlarna = computed(() => norceCheckout.isKlarna.value);
const isWalley = computed(() => norceCheckout.isWalley.value);
const isNonPsp = computed(() => norceCheckout.isNonPsp.value);
const hasMultiplePspAdapters = computed(() => norceCheckout.hasMultiplePspAdapters.value);
const currentAdapter = computed(() => norceCheckout.paymentAdapter.value);
const payment = computed(() => norceCheckout.payment.value);
const orderId = computed(() => norceCheckout.orderId.value);
const paymentId = computed(() => norceCheckout.paymentId.value);
const isInitializing = computed(() => norceCheckout.isInitializing.value);
const isSwitchingAdapter = computed(() => norceCheckout.isSwitchingAdapter.value);
const error = computed(() => norceCheckout.error.value);

// Get PSP adapters (exclude nonpsp for selector)
const pspAdapters = computed(() => {
  return norceCheckout.availableAdapters.value.filter(a => a !== 'nonpsp');
});

// Default adapter labels from translations
const defaultAdapterLabels: Record<string, string> = {
  adyen: $t('checkout.norceCheckout.adapters.adyen'),
  klarna: $t('checkout.norceCheckout.adapters.klarna'),
  walley: $t('checkout.norceCheckout.adapters.walley'),
};

// Get adapter label for display
const getAdapterLabel = (adapter: PaymentAdapter): string => {
  if (props.adapterLabels?.[adapter]) {
    return props.adapterLabels[adapter];
  }
  return defaultAdapterLabels[adapter] || adapter;
};

// Initialize checkout
const initialize = async() => {
  if (!props.basketUuid) { return; }

  // Runs reset + load adapters + initialize as one attempt (mirrors what a fresh
  // remount / "navigate away and back" does).
  const runInitAttempt = async(): Promise<boolean> => {
    // Clear any state left over from a previous checkout session. The composable holds
    // module-level state that survives SPA navigation (unlike a page reload), so after a
    // completed purchase the old session's basketUuid/orderId/adapter can bleed into the
    // new basket and make initialize() fail. Resetting here makes a new session behave
    // exactly like a fresh page load.
    norceCheckout.reset();
    await norceCheckout.loadAvailableAdapters();
    return norceCheckout.initialize(props.basketUuid);
  };

  let success = await runInitAttempt();

  // TEMPORARY STOPGAP (remove once the backend fix lands — see backend ticket):
  // After a completed purchase, the backend carries order/session state that makes the
  // FIRST /initialize of the next basket fail with "Order can not be exported to Norce
  // Commerce if this Payment Method is used", while an identical retry (navigate away and
  // back) succeeds. Until backend clears that state, retry the SAME call a couple of times
  // on this SPECIFIC error only. Any other failure is surfaced immediately as before.
  const EXPORT_ERROR_FRAGMENT = 'can not be exported';
  const MAX_EXPORT_RETRIES = 2;
  const EXPORT_RETRY_DELAY_MS = 1200;
  let exportRetries = 0;
  while (
    !success &&
    exportRetries < MAX_EXPORT_RETRIES &&
    (norceCheckout.error.value ?? '').toLowerCase().includes(EXPORT_ERROR_FRAGMENT)
  ) {
    exportRetries++;
    isRetryingInit.value = true; // hold the loading state so the error screen doesn't flash
    console.warn(`[NC-DEBUG] initialize export-blocked, retrying (${exportRetries}/${MAX_EXPORT_RETRIES}) after ${EXPORT_RETRY_DELAY_MS}ms`);
    await new Promise(resolve => setTimeout(resolve, EXPORT_RETRY_DELAY_MS));
    success = await runInitAttempt();
  }
  isRetryingInit.value = false;

  if (success) {
    emit('initialized');
  }
};

// Retry initialization
const retryInitialize = () => {
  initialize();
};

// Switch payment adapter
const switchAdapter = async(adapter: PaymentAdapter) => {
  if (adapter === currentAdapter.value) { return; }

  const success = await norceCheckout.switchToAdapter(adapter);

  if (success) {
    emit('adapter-changed', adapter);
  } else {
    emit('error', norceCheckout.error.value || 'Failed to switch payment method');
  }
};

// Handle successful payment
const handleSuccess = (basketId: string, order?: NorceOrderResult) => {
  // The paid basket is now a completed order and must not be reused. Clear it here
  // (before the child component navigates to the success page) so navigating back
  // into checkout without a reload creates a fresh basket instead of re-running
  // initialize() against the completed one — which the backend rejects with
  // "Order can not be exported to Norce Commerce if this Payment Method is used".
  cartStore.createNewCart();
  emit('success', basketId, order);
};

// Handle payment error
const handleError = (message: string) => {
  emit('error', message);
};

// Watch for basket UUID changes
watch(
  () => props.basketUuid,
  (newUuid) => {
    if (newUuid && props.autoInitialize) {
      initialize();
    }
  },
  { immediate: true }
);

// Expose methods for parent component
defineExpose({
  initialize,
  switchAdapter,
  refresh: () => norceCheckout.refresh(),
  reset: () => norceCheckout.reset(),
  // Expose suspend/resume for Klarna (useful during cart updates)
  suspendKlarna: () => klarnaCheckoutRef.value?.suspend(),
  resumeKlarna: () => klarnaCheckoutRef.value?.resume(),
});
</script>

<style scoped lang="postcss">
.NorceCheckoutPayment {
  @apply w-full;
}

.adapter-logo {
  @apply h-20 w-auto;
}

.loading-spinner {
  @apply w-24 h-24 border-2 border-light border-t-dark animate-spin;
  border-radius: 50%;
}
</style>
